import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import {
  RefreshCw,
  Search,
  Users,
  MapPin,
  Clock,
  Battery,
  Navigation,
  X,
  ChevronRight,
  Activity,
  Coffee,
  Route,
  Eye,
  EyeOff,
  Circle,
  Wifi,
  WifiOff,
  ChevronDown
} from 'lucide-react';
import fieldTeamApi from '../../services/fieldTeamApi';
import 'leaflet/dist/leaflet.css';
import './LiveMap.css';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Member colors palette
const MEMBER_COLORS = [
  '#00D4FF', '#FF6B6B', '#4ECDC4', '#FFE66D', '#95E1D3',
  '#F38181', '#AA96DA', '#FF9F43', '#74B9FF', '#A29BFE',
  '#55EFC4', '#FD79A8', '#00B894', '#E17055', '#81ECEC',
];

// Date presets
const DATE_PRESETS = [
  { label: 'Today', days: 0 },
  { label: 'Yesterday', days: 1 },
  { label: 'Last 7 Days', days: 7 },
  { label: 'Last 30 Days', days: 30 },
  { label: 'Last 90 Days', days: 90 },
  { label: 'Custom', days: -1 },
];

// Custom pulsing dot icons for moving users
const createDotIcon = (color, isActive = false) => L.divIcon({
  className: 'custom-dot-marker',
  html: `<div class="location-dot ${isActive ? 'pulsing' : ''}" style="--dot-color: ${color};">
    <div class="dot-core"></div>
    ${isActive ? '<div class="dot-pulse"></div><div class="dot-pulse delay"></div>' : ''}
  </div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -12]
});

const activeIcon = createDotIcon('#22c55e', true);
const idleIcon = createDotIcon('#eab308', false);
const offlineIcon = createDotIcon('#6b7280', false);

// Reverse geocoding cache
const addressCache = {};

// Map center helper
function MapCenter({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, zoom || map.getZoom());
  }, [center, zoom, map]);
  return null;
}

// Glowing Polyline
function GlowingPolyline({ positions, color, weight = 4 }) {
  if (!positions || positions.length < 2) return null;
  return (
    <>
      <Polyline positions={positions} pathOptions={{ color, weight: weight + 12, opacity: 0.1, lineCap: 'round', lineJoin: 'round' }} />
      <Polyline positions={positions} pathOptions={{ color, weight: weight + 6, opacity: 0.2, lineCap: 'round', lineJoin: 'round' }} />
      <Polyline positions={positions} pathOptions={{ color, weight: weight + 2, opacity: 0.4, lineCap: 'round', lineJoin: 'round' }} />
      <Polyline positions={positions} pathOptions={{ color, weight, opacity: 1, lineCap: 'round', lineJoin: 'round' }} />
    </>
  );
}

// Reverse geocode coordinates to street address
const reverseGeocode = async (lat, lng) => {
  const cacheKey = `${lat.toFixed(4)},${lng.toFixed(4)}`;
  if (addressCache[cacheKey]) return addressCache[cacheKey];

  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
      { headers: { 'Accept-Language': 'en' } }
    );
    const data = await response.json();

    // Build a readable address
    let address = '';
    if (data.address) {
      const parts = [];
      if (data.address.house_number) parts.push(data.address.house_number);
      if (data.address.road) parts.push(data.address.road);
      if (data.address.neighbourhood) parts.push(data.address.neighbourhood);
      if (data.address.suburb) parts.push(data.address.suburb);
      if (data.address.city || data.address.town) parts.push(data.address.city || data.address.town);
      address = parts.join(', ') || data.display_name?.split(',').slice(0, 3).join(', ') || 'Unknown location';
    } else {
      address = data.display_name?.split(',').slice(0, 3).join(', ') || 'Unknown location';
    }

    addressCache[cacheKey] = address;
    return address;
  } catch (error) {
    console.error('Reverse geocoding failed:', error);
    return 'Unable to get address';
  }
};

function LiveMap() {
  const [locations, setLocations] = useState([]);
  const [status, setStatus] = useState({ active: 0, idle: 0, offline: 0 });
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberRoute, setMemberRoute] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [mapCenter, setMapCenter] = useState([24.8607, 67.0011]);
  const [mapZoom, setMapZoom] = useState(12);
  const refreshIntervalRef = useRef(null);
  const [memberAddresses, setMemberAddresses] = useState({});

  // Routes panel state
  const [showRoutesPanel, setShowRoutesPanel] = useState(false);
  const [memberRoutes, setMemberRoutes] = useState({});
  const [memberVisibility, setMemberVisibility] = useState({});
  const [memberColors, setMemberColors] = useState({});
  const [selectedDatePreset, setSelectedDatePreset] = useState(0);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [snapToRoads, setSnapToRoads] = useState(true); // Snap GPS points to roads using OSRM

  // Members panel state
  const [showMembersPanel, setShowMembersPanel] = useState(true);

  // Assign colors
  useEffect(() => {
    const colors = {};
    locations.forEach((member, index) => {
      colors[member.member_id] = MEMBER_COLORS[index % MEMBER_COLORS.length];
    });
    setMemberColors(colors);
  }, [locations]);

  // Fetch addresses for members with location
  useEffect(() => {
    const fetchAddresses = async () => {
      const membersWithLocation = locations.filter(m => m.latitude && m.longitude);
      for (const member of membersWithLocation) {
        if (!memberAddresses[member.member_id]) {
          const address = await reverseGeocode(parseFloat(member.latitude), parseFloat(member.longitude));
          setMemberAddresses(prev => ({ ...prev, [member.member_id]: address }));
        }
      }
    };
    if (locations.length > 0) {
      fetchAddresses();
    }
  }, [locations]);

  // Track if initial load has been done
  const initialLoadDone = useRef(false);

  // Fetch locations
  const fetchLocations = useCallback(async () => {
    try {
      const [locationsRes, statusRes] = await Promise.all([
        fieldTeamApi.getLiveLocations(),
        fieldTeamApi.getLiveStatus()
      ]);
      if (locationsRes.data.success) {
        setLocations(locationsRes.data.data);
        // Only center map on first load, not on refreshes
        if (!initialLoadDone.current && locationsRes.data.data.length > 0) {
          const first = locationsRes.data.data.find(m => m.status === 'active') || locationsRes.data.data[0];
          if (first.latitude && first.longitude) {
            setMapCenter([parseFloat(first.latitude), parseFloat(first.longitude)]);
          }
          initialLoadDone.current = true;
        }
      }
      if (statusRes.data.success) setStatus(statusRes.data.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Date range calculation
  const getDateRange = useCallback(() => {
    const today = new Date();
    const preset = DATE_PRESETS[selectedDatePreset];
    if (preset.days === -1) return { startDate: customStartDate, endDate: customEndDate };
    const endDate = today.toISOString().split('T')[0];
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - preset.days);
    return { startDate: startDate.toISOString().split('T')[0], endDate };
  }, [selectedDatePreset, customStartDate, customEndDate]);

  // Fetch routes
  const fetchMemberRoutes = useCallback(async () => {
    if (locations.length === 0) return;
    setLoadingRoutes(true);
    try {
      const { startDate, endDate } = getDateRange();
      if (!startDate || !endDate) return;
      const visibleMemberIds = locations.filter(m => memberVisibility[m.member_id] !== false).map(m => m.member_id);
      if (visibleMemberIds.length === 0) { setMemberRoutes({}); return; }

      const response = await fieldTeamApi.getRoutes({
        member_ids: visibleMemberIds.join(','),
        start_date: startDate,
        end_date: endDate,
        snap_to_roads: snapToRoads ? 'true' : 'false'
      });
      if (response.data.success) {
        const routes = {};
        response.data.data.forEach(memberData => {
          const allPoints = [];
          memberData.routes.forEach(route => {
            route.points.forEach(point => {
              allPoints.push([parseFloat(point.latitude), parseFloat(point.longitude)]);
            });
          });
          routes[memberData.member_id] = allPoints;
        });
        setMemberRoutes(routes);
      }
    } catch (error) {
      console.error('Failed to fetch routes:', error);
    } finally {
      setLoadingRoutes(false);
    }
  }, [locations, memberVisibility, getDateRange, snapToRoads]);

  // Fetch single member route
  const fetchMemberRoute = useCallback(async (memberId) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await fieldTeamApi.getMemberRoute(memberId, today);
      if (response.data.success) setMemberRoute(response.data.data.route || []);
    } catch (error) {
      console.error('Failed to fetch route:', error);
      setMemberRoute([]);
    }
  }, []);

  // Effects
  useEffect(() => {
    fetchLocations();
    // Refresh every 10 seconds for more real-time updates
    if (autoRefresh) refreshIntervalRef.current = setInterval(fetchLocations, 10000);
    return () => { if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current); };
  }, [fetchLocations, autoRefresh]);

  // Routes auto-refresh when panel is open
  const routesIntervalRef = useRef(null);
  useEffect(() => {
    if (showRoutesPanel) {
      fetchMemberRoutes();
      // Auto-refresh routes every 15 seconds when panel is open
      routesIntervalRef.current = setInterval(fetchMemberRoutes, 15000);
    }
    return () => { if (routesIntervalRef.current) clearInterval(routesIntervalRef.current); };
  }, [showRoutesPanel, selectedDatePreset, customStartDate, customEndDate, fetchMemberRoutes]);

  useEffect(() => {
    if (locations.length > 0 && Object.keys(memberVisibility).length === 0) {
      const visibility = {};
      locations.forEach(m => { visibility[m.member_id] = true; });
      setMemberVisibility(visibility);
    }
  }, [locations, memberVisibility]);

  // Handlers
  const handleMemberClick = (member) => {
    setSelectedMember(member);
    if (member.latitude && member.longitude) {
      setMapCenter([parseFloat(member.latitude), parseFloat(member.longitude)]);
      setMapZoom(15);
    }
    fetchMemberRoute(member.member_id);
  };

  const clearSelection = () => {
    setSelectedMember(null);
    setMemberRoute([]);
    setMapZoom(12);
  };

  const toggleMemberVisibility = (memberId) => {
    setMemberVisibility(prev => ({ ...prev, [memberId]: !prev[memberId] }));
  };

  const toggleAllMembers = (show) => {
    const visibility = {};
    locations.forEach(m => { visibility[m.member_id] = show; });
    setMemberVisibility(visibility);
  };

  // Filters
  const filteredMembers = locations.filter(member => {
    const matchesSearch = member.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          member.username?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' || member.status === filter;
    return matchesSearch && matchesFilter;
  });

  const getMarkerIcon = (status) => {
    switch (status) {
      case 'active': return activeIcon;
      case 'idle': return idleIcon;
      default: return offlineIcon;
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return 'N/A';
    const diff = Math.floor((new Date() - new Date(dateStr)) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'active': return 'status-active';
      case 'idle': return 'status-idle';
      default: return 'status-offline';
    }
  };

  return (
    <div className="live-map-page">
      {/* Stats Header */}
      <div className="live-map-header">
        <div className="stat-blocks">
          <div className="stat-block green">
            <div className="stat-block-icon"><Activity size={20} /></div>
            <div className="stat-block-content">
              <span className="stat-block-value">{status.active}</span>
              <span className="stat-block-label">Active</span>
            </div>
          </div>
          <div className="stat-block orange">
            <div className="stat-block-icon"><Coffee size={20} /></div>
            <div className="stat-block-content">
              <span className="stat-block-value">{status.idle}</span>
              <span className="stat-block-label">Idle</span>
            </div>
          </div>
          <div className="stat-block red">
            <div className="stat-block-icon"><WifiOff size={20} /></div>
            <div className="stat-block-content">
              <span className="stat-block-value">{status.offline}</span>
              <span className="stat-block-label">Offline</span>
            </div>
          </div>
          <div className="stat-block purple">
            <div className="stat-block-icon"><Users size={20} /></div>
            <div className="stat-block-content">
              <span className="stat-block-value">{locations.length}</span>
              <span className="stat-block-label">Total Members</span>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="live-map-controls">
          <button
            className={`control-btn ${showMembersPanel ? 'active' : ''}`}
            onClick={() => setShowMembersPanel(!showMembersPanel)}
            title="Toggle members panel"
          >
            <Users size={16} />
            Members
          </button>
          <button
            className={`control-btn ${showRoutesPanel ? 'active' : ''}`}
            onClick={() => setShowRoutesPanel(!showRoutesPanel)}
            title="Show historical routes"
          >
            <Route size={16} />
            Routes
          </button>
          <div className="control-divider" />
          <button
            className={`control-btn ${autoRefresh ? 'active' : ''}`}
            onClick={() => setAutoRefresh(!autoRefresh)}
            title={autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          >
            <RefreshCw size={16} className={autoRefresh ? 'spinning' : ''} />
            {autoRefresh ? 'Auto' : 'Manual'}
          </button>
          <button className="control-btn" onClick={fetchLocations} disabled={loading} title="Refresh now">
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          </button>
          {lastUpdated && <span className="last-updated">Updated {formatTimeAgo(lastUpdated)}</span>}
        </div>
      </div>

      {/* Map Container */}
      <div className="live-map-wrapper">
        <div className="map-card">
          {/* Members Sidebar - Inside Map Card */}
          {showMembersPanel && (
            <div className="members-sidebar">
              <div className="sidebar-header">
                <h3>Team Members</h3>
                <button className="close-sidebar-btn" onClick={() => setShowMembersPanel(false)}>
                  <X size={16} />
                </button>
              </div>
              <div className="search-box">
                <Search size={14} />
                <input
                  type="text"
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="filter-tabs">
                {['all', 'active', 'idle', 'offline'].map(f => (
                  <button
                    key={f}
                    className={filter === f ? 'active' : ''}
                    onClick={() => setFilter(f)}
                  >
                    {f === 'all' ? `All (${locations.length})` :
                     f === 'active' ? `Active (${status.active})` :
                     f === 'idle' ? `Idle (${status.idle})` :
                     `Offline (${status.offline})`}
                  </button>
                ))}
              </div>
              <div className="member-list">
                {loading && locations.length === 0 ? (
                  <div className="loading-state"><RefreshCw className="spinning" size={24} /><span>Loading...</span></div>
                ) : filteredMembers.length === 0 ? (
                  <div className="empty-state"><Users size={24} /><span>No members found</span></div>
                ) : (
                  filteredMembers.map((member) => (
                    <div
                      key={member.member_id}
                      className={`member-item ${selectedMember?.member_id === member.member_id ? 'selected' : ''}`}
                      onClick={() => handleMemberClick(member)}
                    >
                      <div className={`member-status-dot ${getStatusClass(member.status)}`} />
                      <div className="member-info">
                        <span className="member-name">{member.name || member.username}</span>
                        <span className="member-time"><Clock size={10} />{formatTimeAgo(member.recorded_at)}</span>
                      </div>
                      <ChevronRight size={14} className="chevron" />
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Routes Panel */}
          {showRoutesPanel && (
            <div className="routes-panel">
              <div className="routes-panel-header">
                <h4><Route size={16} /> Historical Routes</h4>
                <button className="close-btn" onClick={() => setShowRoutesPanel(false)}><X size={16} /></button>
              </div>
              <div className="date-selector">
                <div className="date-presets">
                  {DATE_PRESETS.map((preset, index) => (
                    <button
                      key={preset.label}
                      className={`date-preset-btn ${selectedDatePreset === index ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedDatePreset(index);
                        setShowDatePicker(preset.days === -1);
                      }}
                    >{preset.label}</button>
                  ))}
                </div>
                {showDatePicker && (
                  <div className="custom-date-picker">
                    <div className="date-input-group">
                      <label>From</label>
                      <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
                    </div>
                    <div className="date-input-group">
                      <label>To</label>
                      <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
                    </div>
                    <button className="apply-dates-btn" onClick={fetchMemberRoutes} disabled={!customStartDate || !customEndDate}>Apply</button>
                  </div>
                )}
              </div>
              <div className="route-options">
                <label className="toggle-option">
                  <span>Snap to Roads</span>
                  <button
                    className={`toggle-btn ${snapToRoads ? 'active' : ''}`}
                    onClick={() => setSnapToRoads(!snapToRoads)}
                    title="Snap GPS points to nearest roads using OSRM"
                  >
                    <div className="toggle-slider" />
                  </button>
                </label>
              </div>
              <div className="visibility-controls">
                <div className="visibility-header">
                  <span>Members</span>
                  <div className="visibility-actions">
                    <button onClick={() => toggleAllMembers(true)}>Show All</button>
                    <button onClick={() => toggleAllMembers(false)}>Hide All</button>
                  </div>
                </div>
                <div className="member-visibility-list">
                  {loadingRoutes ? (
                    <div className="loading-routes"><RefreshCw className="spinning" size={16} /><span>Loading routes...</span></div>
                  ) : (
                    locations.map((member) => (
                      <div
                        key={member.member_id}
                        className={`visibility-item ${memberVisibility[member.member_id] ? 'visible' : 'hidden'}`}
                        onClick={() => toggleMemberVisibility(member.member_id)}
                      >
                        <div className="member-color-indicator" style={{ backgroundColor: memberColors[member.member_id] }} />
                        <span className="member-name">{member.name || member.username}</span>
                        {memberVisibility[member.member_id] ? <Eye size={14} className="visibility-icon" /> : <EyeOff size={14} className="visibility-icon" />}
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="route-legend">
                <h5>Legend</h5>
                <div className="legend-items">
                  {locations
                    .filter(m => memberVisibility[m.member_id] && memberRoutes[m.member_id]?.length > 0)
                    .map((member) => (
                      <div key={member.member_id} className="legend-item">
                        <div className="legend-line" style={{ backgroundColor: memberColors[member.member_id] }} />
                        <span>{member.name || member.username}</span>
                        <span className="legend-points">{memberRoutes[member.member_id]?.length || 0} pts</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* Selected Member Panel */}
          {selectedMember && (
            <div className="selected-member-panel">
              <div className="panel-header">
                <div className="member-details">
                  <div className={`status-badge ${getStatusClass(selectedMember.status)}`}>{selectedMember.status}</div>
                  <h4>{selectedMember.name || selectedMember.username}</h4>
                </div>
                <button className="close-btn" onClick={clearSelection}><X size={16} /></button>
              </div>
              <div className="panel-stats">
                <div className="panel-stat address-stat">
                  <MapPin size={14} />
                  <span>{selectedMember.latitude ? (memberAddresses[selectedMember.member_id] || 'Loading address...') : 'No location'}</span>
                </div>
                {selectedMember.accuracy && (
                  <div className="panel-stat"><Navigation size={14} /><span>+/-{Math.round(selectedMember.accuracy)}m accuracy</span></div>
                )}
                {selectedMember.battery_level && (
                  <div className="panel-stat"><Battery size={14} /><span>{selectedMember.battery_level}% battery</span></div>
                )}
                <div className="panel-stat"><Clock size={14} /><span>Last update: {formatTimeAgo(selectedMember.recorded_at)}</span></div>
              </div>
              {memberRoute.length > 0 && (
                <div className="route-info">
                  <span className="route-label">Today's Route</span>
                  <span className="route-points">{memberRoute.length} points</span>
                </div>
              )}
            </div>
          )}

          {/* Map */}
          <MapContainer center={mapCenter} zoom={mapZoom} className="leaflet-map" zoomControl={true}>
            <MapCenter center={mapCenter} zoom={mapZoom} />
            <TileLayer
              attribution='&copy; <a href="https://carto.com/">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            />
            {filteredMembers.map((member) => (
              member.latitude && member.longitude && (
                <Marker
                  key={member.member_id}
                  position={[parseFloat(member.latitude), parseFloat(member.longitude)]}
                  icon={getMarkerIcon(member.status)}
                  eventHandlers={{ click: () => handleMemberClick(member) }}
                >
                  <Popup>
                    <div className="marker-popup">
                      <strong>{member.name || member.username}</strong>
                      <span className={`popup-status ${getStatusClass(member.status)}`}>{member.status}</span>
                      <span className="popup-address">{memberAddresses[member.member_id] || 'Loading...'}</span>
                      <span className="popup-time">Last seen: {formatTimeAgo(member.recorded_at)}</span>
                      {member.battery_level && <span className="popup-battery">Battery: {member.battery_level}%</span>}
                    </div>
                  </Popup>
                </Marker>
              )
            ))}
            {showRoutesPanel && Object.entries(memberRoutes).map(([memberId, points]) => {
              const numericId = parseInt(memberId);
              if (memberVisibility[numericId] === false || !points || points.length < 2) return null;
              return <GlowingPolyline key={memberId} positions={points} color={memberColors[numericId]} weight={4} />;
            })}
            {selectedMember && memberRoute.length > 1 && !showRoutesPanel && (
              <GlowingPolyline
                positions={memberRoute.map(point => [parseFloat(point.latitude), parseFloat(point.longitude)])}
                color={memberColors[selectedMember.member_id] || '#6366f1'}
                weight={4}
              />
            )}
          </MapContainer>
        </div>
      </div>
    </div>
  );
}

export default LiveMap;
